import type { Editor } from "@tiptap/react";
import type { Asset } from "schemas/generated/asset";
import type {
  ChatAssetItem,
  ChatProductItem,
} from "schemas/generated/chatMessage";
import type { Product } from "schemas/generated/product";

import React, { useCallback, useRef, useState } from "react";

import { ContextItemsMenu } from "@editor/components/editor/ai/chat/ContextItemsMenu/ContextItemsMenu";
import { MessageInput } from "@editor/components/editor/ai/chat/MessageInput";
import AssetComponent from "@editor/components/editor/ai/chat/MessageInput/assetNodeExtension";
import ProductComponent from "@editor/components/editor/ai/chat/MessageInput/productNodeExtension";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";

import {
  chatEditorHandleKeyDown,
  chatEditorOnUpdate,
  getCleanedChatPrompt,
} from "@/features/react-editor/components/chat/utils/tiptapUtils";
import Button from "@replo/design-system/components/button/Button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/shadcn/core/tabs";
import twMerge from "@replo/design-system/utils/twMerge";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { AppWindow, BookOpenText, Tag, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PagesSection from "./PagesSection";
import PageTemplatesSection from "./PageTemplatesSection";

const quickPrompts = [
  {
    icon: <AppWindow className="w-3.5 h-3.5" />,
    text: "Build a Landing Page",
  },
  {
    icon: <Tag className="w-3.5 h-3.5" />,
    text: "Build a Product Page",
  },
  {
    icon: <Users className="w-3.5 h-3.5" />,
    text: "Build an About Us Page",
  },
  {
    icon: <BookOpenText className="w-3.5 h-3.5" />,
    text: "Build a Blog Page",
  },
];

function Home() {
  const isReaplo = isFeatureEnabled("reaplo");
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState("pages");
  const [files, setFiles] = useState<File[] | null>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [atPosition, setAtPosition] = useState<number | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const homeEditorRef = useRef<Editor | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const editor = homeEditorRef.current;
      if (!editor || !projectId) {
        return;
      }

      const prompt = getCleanedChatPrompt(editor);
      if (!prompt) {
        return;
      }

      editor.commands.setContent("", false);
      setFiles(null);

      // NOTE (Bobby, 2025-07-30): Storing this through react router's state so in the browser's session history. Potentially want to make this
      // in a shared context but the home and chat were just in pretty unrelated contexts so doing this for now.
      navigate(`/shops/${projectId}/chat`, {
        state: {
          initialMessage: prompt,
          initialFiles: files,
        },
      });
    },
    [projectId, navigate, files],
  );

  // Set up the Tiptap editor
  const homeEditor = useEditor({
    extensions: [
      StarterKit,
      AssetComponent,
      ProductComponent,
      Placeholder.configure({
        placeholder: "Ask Replo anything...",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate(
      contextMenuOpen,
      setAtPosition,
      setContextMenuOpen,
      atPosition,
    ),
    editorProps: {
      attributes: {
        style: "font-size: 14px;",
        class: "home-chat-input",
      },
      handleKeyDown: chatEditorHandleKeyDown(
        contextMenuOpen,
        setAtPosition,
        setContextMenuOpen,
        atPosition,
        handleSubmit,
      ),
    },
  });

  // Update the ref whenever the editor changes
  React.useEffect(() => {
    homeEditorRef.current = homeEditor;
  }, [homeEditor]);

  const handleContextItemSelect = (
    type: "asset" | "product",
    data: ChatAssetItem | ChatProductItem,
  ) => {
    if (!homeEditor) {
      return;
    }

    // Create the appropriate HTML component
    const componentTag =
      type === "asset" ? "asset-component" : "product-component";
    const html = `<${componentTag} data="${JSON.stringify(data).replace(/"/g, "&quot;")}"></${componentTag}>`;

    if (atPosition !== null) {
      // Replace the @ with the component at the tracked position
      homeEditor
        .chain()
        .focus()
        .deleteRange({ from: atPosition, to: atPosition + 1 }) // Delete the @
        .insertContentAt(atPosition, html) // Insert component at @ position
        .run();
    } else {
      // Fallback: just insert the component at cursor position
      homeEditor.chain().focus().insertContent(html).run();
    }

    setContextMenuOpen(false);
    setAtPosition(null);
  };

  const handleAssetSelect = (asset: Asset) => {
    const chatAssetItemData: ChatAssetItem = {
      id: asset.id,
      name: asset.name,
      url: asset.url,
      fileExtension: asset.fileExtension,
    };

    handleContextItemSelect("asset", chatAssetItemData);
  };

  const handleProductSelect = (product: Product) => {
    const productItemData: ChatProductItem = {
      id: product.id,
      title: product.title,
      description: product.description ?? "",
      status: product.status,
      featuredMedia: product.featuredMedia,
      media: product.media,
      options: product.options,
      variants: product.variants,
      sellingPlanGroups: product.sellingPlanGroups,
    };

    handleContextItemSelect("product", productItemData);
  };

  if (!isReaplo) {
    window.location.href = `/projects/${projectId}`;
    return null;
  }

  return (
    <div className="flex h-full w-full flex-col gap-3">
      <style>
        {`
            .home-chat-input.tiptap p.is-editor-empty:first-child::before {
              color: var(--color-slate-500);
              content: attr(data-placeholder);
              float: left;
              height: 0;
              pointer-events: none;
            }
          `}
      </style>
      <div className="w-full mx-auto flex flex-col gap-3 h-full">
        <div className="flex flex-col w-full items-center justify-center gap-8 pb-8">
          <div className="text-4xl font-semibold text-default pt-20">
            What can I help you with?
          </div>
          <div className="flex flex-col w-full items-center justify-center">
            <div className="relative w-11/12 lg:w-1/2 md:w-2/3">
              <form
                ref={formRef}
                onSubmit={(e) => {
                  void handleSubmit(e);
                }}
                className="w-full"
              >
                <MessageInput
                  isChatLoading={false}
                  editor={homeEditor}
                  allowAttachments={true}
                  files={files}
                  setFiles={setFiles}
                  contextMenuOpen={contextMenuOpen}
                  setContextMenuOpen={setContextMenuOpen}
                  editorClassName="min-h-[120px]"
                />
              </form>
              <ContextItemsMenu
                formRef={formRef}
                open={contextMenuOpen}
                onAssetSelect={handleAssetSelect}
                onProductSelect={handleProductSelect}
                position="bottom"
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-3">
              {quickPrompts.map((prompt) => (
                <Button
                  size="base"
                  variant="secondary"
                  key={prompt.text}
                  UNSAFE_className="flex flex-row gap-2"
                  onClick={() => {
                    homeEditor?.commands.setContent(prompt.text, false);
                  }}
                >
                  <div className="pr-2">{prompt.icon}</div>
                  <span className="text-sm">{prompt.text}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-6 h-full">
          <Tabs
            value={selectedTab}
            onValueChange={setSelectedTab}
            defaultValue="pages"
            className="flex flex-col h-full items-center gap-5"
          >
            <TabsList className="grid w-fit grid-cols-2 bg-surface flex-shrink-0 ">
              <TabsTrigger
                className={twMerge(
                  "border-none shadow-none",
                  selectedTab === "pages" &&
                    "bg-white border-0.5 border-border",
                )}
                value="pages"
              >
                Your Pages
              </TabsTrigger>
              <TabsTrigger
                className={twMerge(
                  "border-none shadow-none",
                  selectedTab === "templates" &&
                    "bg-white border-0.5 border-border",
                )}
                value="templates"
              >
                Templates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pages" className="flex-1 min-h-0 w-full">
              <PagesSection />
            </TabsContent>

            <TabsContent value="templates" className="flex-1 min-h-0 w-full">
              <PageTemplatesSection />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default Home;
